/**
 * Notification Service for FlowFrame
 * Handles permission requests, scheduling, and displaying notifications
 */

import { messaging, db, auth } from '../firebase';
import { getToken, onMessage } from 'firebase/messaging';
import { setDoc, doc } from 'firebase/firestore';

// Check if browser supports notifications
const isNotificationSupported = () => {
  return 'Notification' in window && 'serviceWorker' in navigator;
};

// Request notification permission
const requestNotificationPermission = async () => {
  if (!isNotificationSupported()) {
    console.log('Notifications not supported in this browser');
    return false;
  }

  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
};

// Register service worker
const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    console.log('Service workers not supported');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    console.log('Service worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service worker registration failed:', error);
    return null;
  }
};

// Calculate notification time based on performance data and notification settings
const calculateNotificationTime = (performanceDateTime, notification) => {
  const performanceDate = new Date(performanceDateTime);
  
  switch (notification.type) {
    case 'hours-after':
      const hoursAfter = new Date(performanceDate);
      hoursAfter.setHours(hoursAfter.getHours() + notification.value);
      return hoursAfter;
    
    case 'next-day':
      const nextDay = new Date(performanceDate);
      nextDay.setDate(nextDay.getDate() + 1);
      nextDay.setHours(9, 0, 0, 0); // 9 AM
      return nextDay;
    
    case 'days-after':
      const daysAfter = new Date(performanceDate);
      daysAfter.setDate(daysAfter.getDate() + notification.value);
      daysAfter.setHours(9, 0, 0, 0); // 9 AM
      return daysAfter;
      
    default:
      return null;
  }
};


// Register FCM token
const registerFCMToken = async () => {
  try {
    // Request permission first
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.log('Notification permission denied');
      return null;
    }

    // Register the correct service worker
    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    
    // Now get the token with the complete VAPID key
    const token = await getToken(messaging, {
      vapidKey: "BMCcN-K6nhXqOQH5RdwX_-8RK119mg6OdgGE_h6hbLho-HOMwDKpoHDTJh94aXrHM2zB-FZrq66eVPhM-w79yw8",
      serviceWorkerRegistration: registration
    });
    
    if (token) {
      // Save the token to your backend/Firestore
      await saveTokenToFirestore(token);
      return token;
    }
    return null;
  } catch (error) {
    console.error('Error registering FCM token:', error);
    return null;
  }
};

// Save FCM token to Firestore
const saveTokenToFirestore = async (token) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      console.log('User not authenticated');
      return false;
    }
    
    // Save token to user's document
    await setDoc(doc(db, 'users', user.uid), {
      fcmTokens: {
        [token]: {
          createdAt: new Date(),
          deviceInfo: {
            platform: navigator.platform,
            userAgent: navigator.userAgent
          }
        }
      }
    }, { merge: true });
    
    console.log('FCM token saved to Firestore');
    return true;
  } catch (error) {
    console.error('Error saving FCM token:', error);
    return false;
  }
};

// Handle foreground messages
const setupFCMListeners = () => {
  onMessage(messaging, (payload) => {
    console.log('Message received in foreground:', payload);
    
    // Display notification using browser API for foreground notifications
    if (Notification.permission === 'granted' && payload.notification) {
      const { title, body, icon } = payload.notification;
      
      const notif = new Notification(title, {
        body,
        icon: icon || '/logo192.png',
        data: payload.data
      });
      
      notif.onclick = () => {
        window.focus();
        notif.close();
        // Handle click based on data
        if (payload.data && payload.data.url) {
          window.location.href = payload.data.url;
        }
      };
    }
  });
};

// Initialize the notification system
const initNotifications = async () => {
  const hasPermission = await requestNotificationPermission();
  if (!hasPermission) return false;

  const fcmToken = await registerFCMToken();
  setupFCMListeners();
  return !!fcmToken;
};

export {
  isNotificationSupported,
  requestNotificationPermission,
  registerServiceWorker,
  initNotifications,
  calculateNotificationTime,
  registerFCMToken,
  setupFCMListeners
};
